/**
 * Comprehensive Dice Analytics Service
 * Handles context-aware dice roll logging, analytics, and statistics
 * Supports standalone rolls, character rolls, campaign rolls, and session rolls
 */
import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  limit as fbLimit,
  getDocs,
  deleteDoc,
  doc,
  increment,
  getDoc,
  writeBatch
} from 'firebase/firestore'
import { db } from '@/services/firebase/config'
import { useUserStore } from '@/stores/userStore'

class DiceAnalyticsService {
  /**
   * Log a dice roll with full context awareness
   * @param {Object} rollData - Complete roll data with optional context
   * @returns {Promise<Object>} Logged entry with analysis
   */
  static async logRoll(rollData) {
    try {
      const userStore = useUserStore()
      const uid = userStore.user?.uid

      if (!uid) {
        throw new Error('User not authenticated')
      }

      // Analyze roll for critical outcomes and dice types
      const rollAnalysis = this.analyzeRoll(rollData)

      // Create comprehensive log entry with flexible context
      const logEntry = {
        // Core roll data
        expression: rollData.expression || rollData.notation || '',
        result: rollData.result || rollData.total || 0,
        individualRolls: rollData.individualRolls || [],
        modifier: rollData.modifier || 0,
        naturalRolls: rollAnalysis.naturalRolls,
        diceTypes: rollAnalysis.diceTypes,

        // Context (all optional - flexible!)
        characterId: rollData.characterId || null,
        campaignId: rollData.campaignId || null,
        sessionId: rollData.sessionId || null,

        // Roll metadata
        rollType: rollData.rollType || 'random', // attack, skill, damage, save, random
        rollContext: rollData.rollContext || 'general', // specific skill/weapon/spell
        note: rollData.note || '',
        rollSource: rollData.rollSource || 'dice_roller', // character_sheet, dice_roller, campaign_tool

        // Critical analysis
        isCriticalSuccess: rollAnalysis.isCriticalSuccess,
        isCriticalFailure: rollAnalysis.isCriticalFailure,

        // Attribution and timestamps
        userId: uid,
        createdAt: new Date().toISOString()
      }

      // Log to user's dice_rolls subcollection
      const docRef = await addDoc(collection(db, 'users', uid, 'dice_rolls'), logEntry)

      // Update aggregated analytics in parallel (don't wait)
      this.updateAnalytics(uid, logEntry).catch((error) =>
        console.warn('Failed to update analytics:', error)
      )

      return { id: docRef.id, ...logEntry }
    } catch (error) {
      console.error('Error logging dice roll:', error)
      throw error
    }
  }

  /**
   * Analyze roll for critical outcomes and extract dice information
   * @param {Object} rollData - Roll data with individualRolls
   * @returns {Object} Analysis with critical flags and dice info
   */
  static analyzeRoll(rollData) {
    const analysis = {
      isCriticalSuccess: false,
      isCriticalFailure: false,
      naturalRolls: [],
      diceTypes: []
    }

    const rolls = rollData.individualRolls || rollData.rawResults || []
    if (!Array.isArray(rolls)) return analysis

    rolls.forEach((roll) => {
      if (roll && typeof roll === 'object') {
        const rollValue = roll.value || roll.result || roll.roll
        const dieSides = roll.sides || roll.die || roll.faces || 20

        if (rollValue !== undefined) {
          analysis.naturalRolls.push(rollValue)
          analysis.diceTypes.push(`d${dieSides}`)

          // Check for critical success/failure on d20s
          if (dieSides === 20) {
            if (rollValue === 20) analysis.isCriticalSuccess = true
            else if (rollValue === 1) analysis.isCriticalFailure = true
          }
        }
      } else if (typeof roll === 'number') {
        // Handle simple number arrays (assume d20)
        analysis.naturalRolls.push(roll)
        analysis.diceTypes.push('d20')
        if (roll === 20) analysis.isCriticalSuccess = true
        else if (roll === 1) analysis.isCriticalFailure = true
      }
    })

    return analysis
  }

  /**
   * Update aggregated analytics for user, character, campaign, session
   * @param {string} userId - User ID
   * @param {Object} logEntry - Complete log entry
   */
  static async updateAnalytics(userId, logEntry) {
    const batch = writeBatch(db)
    const timestamp = new Date()
    const monthKey = timestamp.toISOString().substring(0, 7) // YYYY-MM

    try {
      // 1. Update user lifetime analytics
      const userAnalyticsRef = doc(db, 'stats', 'dice_rolls', 'users', userId)
      batch.set(
        userAnalyticsRef,
        {
          userId: userId, // Store user ID for reference
          lifetime: {
            totalRolls: increment(1),
            criticalSuccesses: increment(logEntry.isCriticalSuccess ? 1 : 0),
            criticalFailures: increment(logEntry.isCriticalFailure ? 1 : 0),
            lastActive: timestamp.toISOString()
          },
          [`monthly.${monthKey}`]: {
            totalRolls: increment(1),
            criticalSuccesses: increment(logEntry.isCriticalSuccess ? 1 : 0),
            criticalFailures: increment(logEntry.isCriticalFailure ? 1 : 0)
          }
        },
        { merge: true }
      )

      // 2. Update character analytics if character context exists
      if (logEntry.characterId) {
        const characterAnalyticsRef = doc(
          db,
          'stats',
          'dice_rolls',
          'characters',
          logEntry.characterId
        )
        batch.set(
          characterAnalyticsRef,
          {
            characterId: logEntry.characterId, // Store character ID for reference
            totalRolls: increment(1),
            criticalSuccesses: increment(logEntry.isCriticalSuccess ? 1 : 0),
            criticalFailures: increment(logEntry.isCriticalFailure ? 1 : 0),
            [`rollTypes.${logEntry.rollType}`]: increment(1),
            [`rollContexts.${logEntry.rollContext}`]: increment(1),
            lastActive: timestamp.toISOString(),
            userId: userId // Track ownership
          },
          { merge: true }
        )
      }

      // 3. Update campaign analytics if campaign context exists
      if (logEntry.campaignId) {
        const campaignAnalyticsRef = doc(
          db,
          'stats',
          'dice_rolls',
          'campaigns',
          logEntry.campaignId
        )
        batch.set(
          campaignAnalyticsRef,
          {
            campaignId: logEntry.campaignId, // Store campaign ID for reference
            totalRolls: increment(1),
            criticalSuccesses: increment(logEntry.isCriticalSuccess ? 1 : 0),
            criticalFailures: increment(logEntry.isCriticalFailure ? 1 : 0),
            [`players.${userId}`]: {
              character: logEntry.characterId,
              rolls: increment(1),
              criticals: increment(logEntry.isCriticalSuccess || logEntry.isCriticalFailure ? 1 : 0)
            },
            lastActive: timestamp.toISOString()
          },
          { merge: true }
        )
      }

      // 4. Update session analytics if session context exists
      if (logEntry.sessionId) {
        const sessionAnalyticsRef = doc(db, 'stats', 'dice_rolls', 'sessions', logEntry.sessionId)
        batch.set(
          sessionAnalyticsRef,
          {
            sessionId: logEntry.sessionId, // Store session ID for reference
            totalRolls: increment(1),
            criticalSuccesses: increment(logEntry.isCriticalSuccess ? 1 : 0),
            criticalFailures: increment(logEntry.isCriticalFailure ? 1 : 0),
            [`players.${userId}`]: {
              character: logEntry.characterId,
              rolls: increment(1),
              criticals: increment(logEntry.isCriticalSuccess || logEntry.isCriticalFailure ? 1 : 0)
            },
            campaignId: logEntry.campaignId,
            lastActive: timestamp.toISOString()
          },
          { merge: true }
        )
      }

      await batch.commit()
    } catch (error) {
      console.error('Error updating analytics:', error)
      // Don't throw - analytics failures shouldn't break dice rolling
    }
  }

  /**
   * Get user statistics across all contexts
   * @param {string} userId - User ID
   * @param {string} timeRange - '7d', '30d', '90d', or 'lifetime'
   * @returns {Promise<Object>} User statistics
   */
  static async getUserStats(userId, timeRange = 'lifetime') {
    try {
      const analyticsDoc = await getDoc(doc(db, 'stats', 'dice_rolls', 'users', userId))

      if (!analyticsDoc.exists()) {
        return this.getEmptyStats(timeRange, { userId })
      }

      const data = analyticsDoc.data()

      if (timeRange === 'lifetime') {
        return {
          timeRange,
          userId,
          ...data.lifetime,
          ...this.calculateRates(data.lifetime)
        }
      }

      // Handle monthly aggregations for time ranges
      const monthKey = new Date().toISOString().substring(0, 7)
      const monthData = data.monthly?.[monthKey] || {}

      return {
        timeRange,
        userId,
        ...monthData,
        ...this.calculateRates(monthData)
      }
    } catch (error) {
      console.error('Error getting user stats:', error)
      return this.getEmptyStats(timeRange, { userId })
    }
  }

  /**
   * Get character statistics
   * @param {string} characterId - Character ID
   * @returns {Promise<Object>} Character statistics
   */
  static async getCharacterStats(characterId) {
    try {
      const analyticsDoc = await getDoc(doc(db, 'stats', 'dice_rolls', 'characters', characterId))

      if (!analyticsDoc.exists()) {
        return this.getEmptyStats('lifetime', { characterId })
      }

      const data = analyticsDoc.data()
      return {
        characterId,
        ...data,
        ...this.calculateRates(data)
      }
    } catch (error) {
      console.error('Error getting character stats:', error)
      return this.getEmptyStats('lifetime', { characterId })
    }
  }

  /**
   * Get campaign statistics
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<Object>} Campaign statistics
   */
  static async getCampaignStats(campaignId) {
    try {
      const analyticsDoc = await getDoc(doc(db, 'stats', 'dice_rolls', 'campaigns', campaignId))

      if (!analyticsDoc.exists()) {
        return this.getEmptyStats('lifetime', { campaignId })
      }

      const data = analyticsDoc.data()
      return {
        campaignId,
        ...data,
        ...this.calculateRates(data),
        partyStats: this.calculatePartyStats(data.players || {})
      }
    } catch (error) {
      console.error('Error getting campaign stats:', error)
      return this.getEmptyStats('lifetime', { campaignId })
    }
  }

  /**
   * Get session statistics
   * @param {string} sessionId - Session ID
   * @returns {Promise<Object>} Session statistics
   */
  static async getSessionStats(sessionId) {
    try {
      const analyticsDoc = await getDoc(doc(db, 'stats', `session_analytics_${sessionId}`))

      if (!analyticsDoc.exists()) {
        return this.getEmptyStats('session', { sessionId })
      }

      const data = analyticsDoc.data()
      return {
        sessionId,
        ...data,
        ...this.calculateRates(data),
        playerStats: this.calculatePartyStats(data.players || {})
      }
    } catch (error) {
      console.error('Error getting session stats:', error)
      return this.getEmptyStats('session', { sessionId })
    }
  }

  /**
   * Get detailed roll history with context filtering
   * @param {Object} filters - Filter options
   * @param {number} limit - Maximum rolls to return
   * @returns {Promise<Array>} Array of roll entries
   */
  static async getRollHistory(filters = {}, limit = 50) {
    try {
      const userStore = useUserStore()
      const uid = userStore.user?.uid

      if (!uid) return []

      // Start with the base collection
      let q = collection(db, 'users', uid, 'dice_rolls')

      // Apply filters first, before orderBy
      const constraints = []

      if (filters.rogueRolls) {
        // Filter for "rogue" rolls - those without character, campaign, or session context
        // We need to filter for rolls where these fields are null or don't exist
        // Since Firestore doesn't support "is null" queries efficiently, we'll filter in memory
        console.log('Filtering for rogue rolls (no context)')
      } else {
        // Apply specific context filters
        if (filters.characterId) {
          constraints.push(where('characterId', '==', filters.characterId))
        }
        if (filters.campaignId) {
          constraints.push(where('campaignId', '==', filters.campaignId))
        }
        if (filters.sessionId) {
          constraints.push(where('sessionId', '==', filters.sessionId))
        }
        if (filters.rollType) {
          constraints.push(where('rollType', '==', filters.rollType))
        }
      }

      // Add orderBy and limit after filters
      constraints.push(orderBy('createdAt', 'desc'))
      constraints.push(fbLimit(limit))

      // Build the query with all constraints
      q = query(q, ...constraints)

      const querySnapshot = await getDocs(q)
      let results = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }))

      // If filtering for rogue rolls, filter out rolls with context in memory
      if (filters.rogueRolls) {
        results = results.filter((roll) => !roll.characterId && !roll.campaignId && !roll.sessionId)
        console.log(
          `Found ${results.length} rogue rolls out of ${querySnapshot.docs.length} total rolls`
        )
      }

      return results
    } catch (error) {
      console.error('Error getting roll history:', error)
      return []
    }
  }

  /**
   * Calculate derived statistics rates
   * @param {Object} stats - Raw statistics
   * @returns {Object} Calculated rates
   */
  static calculateRates(stats) {
    const totalRolls = stats.totalRolls || 0
    const criticalSuccesses = stats.criticalSuccesses || 0
    const criticalFailures = stats.criticalFailures || 0

    return {
      criticalSuccessRate: totalRolls > 0 ? (criticalSuccesses / totalRolls) * 100 : 0,
      criticalFailureRate: totalRolls > 0 ? (criticalFailures / totalRolls) * 100 : 0,
      totalCriticals: criticalSuccesses + criticalFailures,
      criticalRate: totalRolls > 0 ? ((criticalSuccesses + criticalFailures) / totalRolls) * 100 : 0
    }
  }

  /**
   * Calculate party/player statistics
   * @param {Object} players - Player data object
   * @returns {Object} Aggregated party stats
   */
  static calculatePartyStats(players) {
    const playerIds = Object.keys(players)
    const totalPlayers = playerIds.length

    if (totalPlayers === 0) {
      return { totalPlayers: 0, mostActivePlayer: null, avgRollsPerPlayer: 0 }
    }

    let mostActivePlayer = null
    let maxRolls = 0
    let totalRolls = 0

    playerIds.forEach((playerId) => {
      const playerRolls = players[playerId].rolls || 0
      totalRolls += playerRolls

      if (playerRolls > maxRolls) {
        maxRolls = playerRolls
        mostActivePlayer = playerId
      }
    })

    return {
      totalPlayers,
      mostActivePlayer,
      maxRolls,
      avgRollsPerPlayer: totalRolls / totalPlayers,
      totalRolls
    }
  }

  /**
   * Get empty statistics object
   * @param {string} timeRange - Time range identifier
   * @param {Object} context - Context identifiers
   * @returns {Object} Empty stats object
   */
  static getEmptyStats(timeRange, context = {}) {
    return {
      timeRange,
      ...context,
      totalRolls: 0,
      criticalSuccesses: 0,
      criticalFailures: 0,
      criticalSuccessRate: 0,
      criticalFailureRate: 0,
      totalCriticals: 0,
      criticalRate: 0
    }
  }

  /**
   * Clear roll history for a user
   * @param {Object} filters - Filter what to clear
   */
  static async clearRollHistory(filters = {}) {
    try {
      const userStore = useUserStore()
      const uid = userStore.user?.uid

      if (!uid) throw new Error('User not authenticated')

      let q = query(collection(db, 'users', uid, 'dice_rolls'))

      // Apply filters for selective clearing
      if (filters.characterId) {
        q = query(q, where('characterId', '==', filters.characterId))
      }
      if (filters.campaignId) {
        q = query(q, where('campaignId', '==', filters.campaignId))
      }

      const querySnapshot = await getDocs(q)
      const deletePromises = querySnapshot.docs.map((doc) => deleteDoc(doc.ref))

      await Promise.all(deletePromises)
    } catch (error) {
      console.error('Error clearing roll history:', error)
      throw error
    }
  }
}

export default DiceAnalyticsService
